import { Component, Input, computed, inject, signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SensoryAxis } from '../../../../../core/models/editorialContent.model';

/** One drawn axis: label and level resolved, plus the exact figure kept for assistive text. */
interface RenderedAxis {
  code:      string;
  label:     string;
  /** 0-5 normalised level; drives the texture density and the wording. */
  level:     number;
  /** How far the texture reaches across the strip, as a CSS length. Extent, not a figure. */
  extent:    string;
  /** Named intensity. This IS the value as far as the sighted reader is concerned. */
  word:      string;
  /** "Cuerpo: Medio (3 de 5)". The only place the exact figure survives. */
  ariaLabel: string;
}

/**
 * Sensory profile, drawn as ALIGNED ROWS of grain: intensity is the density and the extent of a
 * texture, read across a shared axis.
 *
 * It replaces a bar with a "3/5" printed next to it. That pairing said the same thing twice and
 * still left the reader to work out what a 3 tastes like, so three things changed:
 *
 *  - the level is NAMED ("Dominante", "Medio", "Sin presencia"). The word is the reading;
 *  - the texture also EXTENDS across the strip in proportion to the level. Density alone has no
 *    reference to be read against, while extent is an analog quantity nobody has to be taught;
 *  - the strips share one left edge and one width, so comparing two axes is pre-attentive. The
 *    scattered tiles this replaces made every axis a separate decoding job.
 *
 * The numeral is gone from the screen but kept in the aria-label, which is the one place the
 * exact figure is worth anything: a screen reader cannot compare two densities, and a sighted
 * reader does not need to.
 *
 * Extent and density are perceived rather than counted, which is honest about the precision on
 * offer: the methodology supports "this is more juniper-forward than that", not "this is exactly
 * a 4". A legend strip above the rows shows the same texture running sparse to dense between
 * the lexicon's own poles (Leve -> Dominante), so the code is taught by being shown rather than
 * explained; it is decorative because every row already carries its level in words.
 *
 * Two rules stay structural, not cosmetic:
 *
 *  - an axis the backend did not send is not drawn at all. Drawing it at 0 would turn "we do
 *    not know" into "the trait is absent", which is a different claim;
 *  - a value of 0 IS drawn, as an empty dashed strip labelled "Sin presencia", because a real
 *    absence is information (a Speyside with smoke 0 is being described, not skipped).
 */
@Component({
  selector: 'app-spirit-sensory-profile',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './spirit-sensory-profile.component.html',
  styleUrl: './spirit-sensory-profile.component.scss'
})
export class SpiritSensoryProfileComponent {

  private translate = inject(TranslateService);

  private source = signal<SensoryAxis[]>([]);

  @Input({ required: true }) set axes(value: SensoryAxis[] | null | undefined) {
    this.source.set(value ?? []);
  }

  /**
   * Ordered by intensity, strongest first: the profile should open with what the spirit actually
   * tastes of, not with whichever axis the methodology happens to list first.
   */
  rendered = computed<RenderedAxis[]>(() =>
    [...this.source()]
      .sort((a, b) => (b.value / b.max) - (a.value / a.max))
      .map(axis => {
        const label = this.labelOf(axis);
        const level = this.levelOf(axis);
        const extent = Math.round((axis.value / axis.max) * 100) + '%';
        const word = this.translate.instant('editorial.intensity.' + level);
        const exact = this.translate.instant('editorial.axis_value', {
          value: axis.value, max: axis.max
        });

        return {
          code: axis.axis, label, level, extent, word,
          ariaLabel: `${label}: ${word} (${exact})`
        };
      })
  );

  /**
   * Level 0-5 normalised against the axis max, so a 0-3 axis never lands between two words. A
   * real zero stays a zero: it is the only value that may map to "Sin presencia".
   */
  private levelOf(axis: SensoryAxis): number {
    return Math.round((axis.value / axis.max) * 5);
  }

  /**
   * Localised label when the locale knows the axis, and the methodology label otherwise. The
   * backend already sends a canonical (Spanish) label, so a new axis reaches the sheet named
   * rather than as a raw code while the translation catches up.
   */
  private labelOf(axis: SensoryAxis): string {
    const key = 'editorial.axis.' + axis.axis;
    const translated = this.translate.instant(key);

    return translated && translated !== key ? translated : axis.label;
  }

  /** The legend runs between the poles of the same lexicon the rows use. */
  legendCells = [1, 2, 3, 4, 5];
}
